import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  type ReactElement,
} from "react";
import { useAgent } from "agents/react";
// import { useAgentChat } from "agents/ai-react";
import { useAgentChat } from "@cloudflare/ai-chat/react";
import iconAnswer from "./assets/icon-answer.svg";
import iconFlagResponse from "./assets/icon-flag-response.svg";
import type { UIMessage } from "@ai-sdk/react";
import type { tools } from "./tools";
import type { chatState } from "./types/state";
import type { SubmitData } from "./types/submitData";
// import { isToolUIPart } from "ai";
import { isStaticToolUIPart, isToolUIPart } from "ai";

// Component imports
import { Button } from "@/components/button/Button";
import { Card } from "@/components/card/Card";
import { Avatar } from "@/components/avatar/Avatar";
import { Textarea } from "@/components/textarea/Textarea";
import { MemoizedMarkdown } from "@/components/memoized-markdown";
import { ToolInvocationCard } from "@/components/tool-invocation-card/ToolInvocationCard";
import { LoadingMessageQueue } from "@/components/loader/LoadingMessageQueue";
import Drawer from "./components/drawer/drawer";

import {
  PaperPlaneTiltIcon as PaperPlaneTilt,
  StopIcon as Stop,
  XIcon as X,
  ListIcon as List,
} from "@phosphor-icons/react";

import { useUser } from "@/providers/UserProvider";

// List of tools that require human confirmation
// NOTE: this should match the keys in the executions object in tools.ts
const toolsRequiringConfirmation: (keyof typeof tools)[] = [
  // "getWeatherInformation",
  // "addNumbers",
];

// const randomId = crypto.randomUUID();

export default function Chat({
  authToken,
  randomId = null,
  loadConversationId
}: {
  authToken?: string;
  randomId?: string | null;
  loadConversationId?: string | null;
}) {
  const [textareaHeight, setTextareaHeight] = useState("auto");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<chatState>({
    toolMeta: {},
    currentConversationId: null,
    isMember: false,
  });
  const [loadingConversationId, setLoadingConversationId] = useState<
    number | null
  >(null);
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);
  const [errorOutput, setErrorOutput] = useState<string | false>(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const { userEmail, isMobile } = useUser();
  const agentId = userEmail || (randomId as string); // Use email as agent ID if available, otherwise use random ID
  const [optimisticFlaggedIds, setOptimisticFlaggedIds] = useState<number[]>([]);
  const agentName = authToken ? "authenticatedChat" : "chat";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const loadedFromPropRef = useRef<number | null>(null);


  const defaultErrorOutput = `There was an issue getting your response, please try again.
  If the error continues, please start a new chat.`;

  const botName: string | false = "John"; // Name of the AI chatbot - set to false if you don't want a name displayed

  // Scroll to bottom on mount
  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const agentQuery = useMemo(
    () =>
      authToken
        ? {
            token: authToken,
            ...(isMobile ? { mobile: "1" } : {}),
          }
        : undefined,
    [authToken, isMobile]
  );

  const handleStateUpdate = useCallback((newState: unknown) => {
    const parsedState = newState as chatState;

    setState({
      ...parsedState,
      currentConversationId:
        typeof parsedState.currentConversationId === "number"
          ? parsedState.currentConversationId
          : null,
    });
  }, []);

  const handleAgentError = useCallback((event: Event) => {
    // Log errors, but do not treat all websocket errors as reconnect state.
    console.error("Agent WebSocket error:", event);
  }, []);

  const handleAgentClose = useCallback((event: CloseEvent) => {
    console.warn("Agent WebSocket closed:", {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    });
  }, []);

  const handleAgentOpen = useCallback(() => {
    console.log("Agent WebSocket opened");
  }, []);

  const agent = useAgent({
    agent: agentName,
    name: agentId,
    query: agentQuery,
    onStateUpdate: handleStateUpdate,
    onError: handleAgentError,
    onClose: handleAgentClose,
    onOpen: handleAgentOpen,
    onConnectionError: (error) => {
      console.error("Permanent agent connection error:", error);
      setIsReconnecting(false);
      setErrorOutput(
        error.reason
          ? `Connection failed: ${error.reason}`
          : "Unable to connect to the chat service."
      );
    },
  });

  const [agentInput, setAgentInput] = useState("");

  const isLoadingConversation = loadingConversationId !== null;
  const targetConversationId = useMemo(() => {
    if (!loadConversationId) return null;

    const id = Number(loadConversationId);
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [loadConversationId]);

  const showConversationLoader =
    isLoadingConversation ||
    (targetConversationId !== null &&
      loadedFromPropRef.current !== targetConversationId &&
      !errorOutput &&
      (!agent.identified || state.currentConversationId !== targetConversationId));

  const handleAgentInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setAgentInput(e.target.value);
  };

  const {
    messages: agentMessages,
    //input: agentInput,
    //handleInputChange: handleAgentInputChange,
    sendMessage,
    addToolResult,
    clearHistory,
    stop,
    error,
    status,
  } = useAgentChat<
    unknown,
    UIMessage<{ createdAt: string; chatLogId?: number }>
  >({
    agent,
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });

  const hasConnectedRef = useRef(false);
  const connectionDroppedRef = useRef(false);
  const reconnectOverlayTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectOverlayTimerRef.current !== null) {
        window.clearTimeout(reconnectOverlayTimerRef.current);
        reconnectOverlayTimerRef.current = null;
      }
    };

    if (agent.identified) {
      const recoveredFromDisconnect = connectionDroppedRef.current;

      hasConnectedRef.current = true;
      connectionDroppedRef.current = false;

      clearReconnectTimer();
      setIsReconnecting(false);

      if (recoveredFromDisconnect) {
        // Reset a request orphaned by a disconnected websocket.
        stop();
      }

      return clearReconnectTimer;
    }

    // identified=false during initial connect; do not show reconnect overlay.
    if (!hasConnectedRef.current) {
      return clearReconnectTimer;
    }

    connectionDroppedRef.current = true;

    // Avoid flashing for short interruptions.
    clearReconnectTimer();
    reconnectOverlayTimerRef.current = window.setTimeout(() => {
      setIsReconnecting(true);
    }, 1000);

    return clearReconnectTimer;
  }, [agent.identified, stop]);

  const submitHandler = async (
    e: React.FormEvent<HTMLFormElement> | React.FormEvent
  ) => {
    const data: SubmitData = {
      authToken: authToken || undefined,
    };
    e.preventDefault();
    if (!agentInput.trim()) return;

    const message = agentInput;
    setAgentInput("");

    agent.setState({
      ...state,
      loadingMessage: null,
    });
    setErrorOutput(false); // Clear error output in UI
    // Send message to agent
    await sendMessage(
      {
        role: "user",
        parts: [{ type: "text", text: message }],
        metadata: {
          createdAt: Date.now().toString(),
        } as UIMessage<{ createdAt: string }>["metadata"],
      },
      {
        body: data,
      }
    );
  };

  const stopHandler = (e: React.FormEvent) => {
    e.preventDefault();
    stop();
    // add the abort part to the last message, if its an assistant message
    const lastMessage = agentMessages[agentMessages.length - 1];
    if (lastMessage && lastMessage.role === "assistant") {
      lastMessage.parts = [
        ...(lastMessage.parts || []),
        { type: "text", text: "__ABORT__" },
      ]; // Add abort part
    }
  };

  const newChatHandler = useCallback(() => {
    clearHistory();
    setLoadingConversationId(null);
    setErrorOutput(false);

    const nextState: chatState = {
      ...state,
      currentConversationId: null,
      loadingMessage: null,
    };

    setState(nextState);
    agent.setState(nextState);
  }, [clearHistory, agent, state]);

  useEffect(() => {
    if (typeof state.currentConversationId !== "number") return;

    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "FETCH_CONVERSATIONS" })
    );
  }, [state.currentConversationId]);

  // conversation loading handler - loads the conversation and sets loading state until messages are loaded
  const loadConvoHandler = useCallback(
    (convoId: number) => {
      setErrorOutput(false);
      setLoadingConversationId(convoId);
      clearHistory();

      agent.send(
        JSON.stringify({ type: "load-conversation", conversationId: convoId })
      );
    },
    [clearHistory, agent]
  );

  const clearConversationIdQueryParam = useCallback(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("conversationId")) return;
    url.searchParams.delete("conversationId");
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
  }, []);

  // Load a conversation if the prop is provided via url paramns
  useEffect(() => {
    if (!agent.identified) return;
    if (!loadConversationId) return;

    const convoId = Number(loadConversationId);
    if (!Number.isInteger(convoId) || convoId <= 0) return;

    // avoid duplicate load (StrictMode/effect re-runs/reconnect)
    if (loadedFromPropRef.current === convoId) return;

    // optional: skip if already showing that convo
    if (state.currentConversationId === convoId) {
      loadedFromPropRef.current = convoId;
      clearConversationIdQueryParam();
      return;
    }

    loadedFromPropRef.current = convoId;
    loadConvoHandler(convoId);
    clearConversationIdQueryParam();
  }, [
    loadConversationId,
    agent.identified,
    loadConvoHandler,
    clearConversationIdQueryParam,
    state.currentConversationId,
  ]);

  // Listen for LOAD_CONVERSATION messages dispatched from Expo webview
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type === "LOAD_CONVERSATION") {
          loadConvoHandler(Number(data.id));
        }
        if (data?.type === "NEW_CHAT") {
          newChatHandler();
        }
      } catch {
        // ignore non-JSON messages
      }
    };
    window.addEventListener("message", handleMessage);
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "WEBVIEW_READY" })
    );
    return () => window.removeEventListener("message", handleMessage);
  }, [loadConvoHandler, newChatHandler]);

  useEffect(() => {
    if (
      loadingConversationId !== null &&
      state.currentConversationId === loadingConversationId &&
      agentMessages.length > 0
    ) {
      setLoadingConversationId(null);
    }
  }, [loadingConversationId, state.currentConversationId, agentMessages.length]);

  useEffect(() => {
    if (error || state.error) {
      setErrorOutput(state.error || defaultErrorOutput);
      console.error("Error in agent chat:", state.error || error);
    }
  }, [error, state.error]);

  // Scroll to bottom when messages change
  useEffect(() => {
    agentMessages.length > 0 && scrollToBottom();
    //setErrorOutput(false); // Reset error output when messages change
  }, [agentMessages.length, scrollToBottom]);

  // Deduplicate messages with the same toolCallId
  const deduplicatedMessages = agentMessages.filter((message, index) => {
    // Only process assistant messages
    if (message.role !== "assistant") return true;

    // Get all toolCallIds in this message
    const currentToolCallIds =
      message.parts?.filter(isToolUIPart).map((part) => part.toolCallId) || [];

    // If no tool calls, keep the message
    if (currentToolCallIds.length === 0) return true;

    // Check if any later message has the same toolCallId
    const hasDuplicateInLaterMessage = agentMessages
      .slice(index + 1)
      .some((laterMessage) => {
        if (laterMessage.role !== "assistant") return false;
        const laterToolCallIds =
          laterMessage.parts
            ?.filter(isToolUIPart)
            .map((part) => part.toolCallId) || [];
        return currentToolCallIds.some((id) => laterToolCallIds.includes(id));
      });

    // Keep only if no duplicate exists in a later message
    return !hasDuplicateInLaterMessage;
  });

  // useEffect(() => {
  //   console.log(
  //     "messages snapshot:",
  //     JSON.parse(JSON.stringify(agentMessages))
  //   );
  // }, [agentMessages]);

  const pendingToolCallConfirmation = agentMessages.some((m) =>
    m.parts?.some(
      (part) =>
        isStaticToolUIPart(part) &&
        part.state === "input-available" &&
        toolsRequiringConfirmation.includes(
          part.type.replace("tool-", "") as keyof typeof tools
        )
    )
  );

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const closeWindow = () => {
    window.parent.postMessage("sc-closeIframe", "*");
  };

  const getBotName = (
    isUser: boolean,
    showAvatar: boolean
  ): ReactElement | null => {
    if (!isUser && showAvatar && botName) {
      return <div className="assistant-name">{botName} </div>;
    }
    return null;
  };

  const messageIsAborted = (message: UIMessage): boolean => {
    return (
      message.parts?.some(
        (part) =>
          part.type === "text" &&
          typeof part.text === "string" &&
          part.text.includes("__ABORT__")
      ) ?? false
    );
  };

  useEffect(() => {
    if (loadingConversationId === null) return;

    const timeout = window.setTimeout(() => {
      setLoadingConversationId(null);
      setErrorOutput("Failed to load conversation. Please try again.");
    }, 5000);

    return () => window.clearTimeout(timeout);
  }, [loadingConversationId]);

  return (
    <div className="h-[100vh] w-full flex justify-center items-center bg-fixed overflow-hidden">
      {/* Main Chat Container */}
      <div
        className={`h-[100vh] w-full mx-auto ${!isMobile ? "max-w-lg" : ""} flex flex-col ${!isMobile ? "rounded-md" : ""} overflow-hidden relative ${isMobile ? "" : "border border-neutral-300 bg-white"}`}
      >
        {!isMobile ? (
          <div className="chat-bot-header px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
            <Button
              type="button"
              variant="ghost"
              size="md"
              shape="square"
              className="rounded-full h-9 w-9"
              onClick={() => setDrawerOpen(!drawerOpen)}
            >
              <List size={22} color="black" />
            </Button>
            <div className="flex-1">
              <h2 className="mt-1 font-bold text-lg text-headline">
                Kois Center Collective Intelligence
              </h2>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="md"
              shape="square"
              className="rounded-full h-9 w-9"
              // parent window - assuming iframe context
              onClick={closeWindow}
            >
              <X size={20} color="black" />
            </Button>
          </div>
        ) : null}
        <div className="output-input-container relative flex-1">
          {!isMobile ? (
            <Drawer
              open={drawerOpen}
              onOpenChange={setDrawerOpen}
              newChatHandler={newChatHandler}
              status={status}
              loadConvoHandler={loadConvoHandler}
              currentConversationId={
                typeof state.currentConversationId === "number"
                  ? state.currentConversationId
                  : null
              }
            />
          ) : null}
          {/* Messages */}
          <div
            className={`h-[calc(100%-160px)] overflow-y-auto p-4 space-y-4 max-h-[calc(100vh-10rem)] ${isMobile ? "landscape:px-16" : ""}`}
          >
            {showConversationLoader ? (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <Card className="p-6 bg-white w-3/4">
                  <div className="flex flex-col items-center gap-4">
                    <span className="animate-spin inline-block w-8 h-8 border-4 border-neutral-300 border-t-transparent rounded-full" />
                    <p className="text-lg font-semibold">Loading...</p>
                  </div>
                </Card>
              </div>
            ) : null}

            {!showConversationLoader && deduplicatedMessages.length === 0 ? (
              (
                <div className="h-full flex items-center justify-center">
                  <Card className="max-w-md mx-auto intro-bg">
                    <div className="text-center">
                      <div className="rounded-full inline-flex mb-2">
                        <img src={iconAnswer} alt="Answer Icon" />
                      </div>
                      <h3 className="text-balance font-bold text-lg">
                        Welcome to the Kois Center Collective Intelligence
                      </h3>
                      <p className="text-muted-foreground">
                        Start a conversation with your AI assistant.
                      </p>
                    </div>
                  </Card>
                </div>
              )
            ) : null}

            {!showConversationLoader && deduplicatedMessages.map((m, index) => {
              if (messageIsAborted(m) || m.role === "system") {
                return null; // Skip rendering this message
              }
              const isUser = m.role === "user";
              const showAvatar =
                index === 0 || deduplicatedMessages[index - 1]?.role !== m.role;
              const isLastMessage = index === deduplicatedMessages.length - 1;

              return (
                <div key={m.id}>
                  {isLastMessage ? <div ref={messagesEndRef} /> : null}
                  <div
                    className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`flex gap-2 max-w-[85%] ${
                        isUser ? "flex-row-reverse" : "flex-row"
                      }`}
                    >
                      {showAvatar && !isUser ? (
                        <>
                          <Avatar username={"AI"} />
                        </>
                      ) : (
                        !isUser && <div className="w-8" />
                      )}

                      <div>
                        {getBotName(isUser, showAvatar)}
                        <div>
                          {m.parts?.map((part, i) => {
                            if (part.type === "text") {
                              return (
                                // biome-ignore lint/suspicious/noArrayIndexKey: immutable index
                                <div key={i}>
                                  <Card
                                    className={`p-3 rounded-md user-bg ${
                                      isUser
                                        ? "rounded-br-none"
                                        : "rounded-bl-none border-assistant-border assistant-bg"
                                    } ${
                                      part.text.startsWith("scheduled message")
                                        ? "border-accent/50"
                                        : ""
                                    } relative`}
                                  >
                                    {part.text.startsWith(
                                      "scheduled message"
                                    ) && (
                                      <span className="absolute -top-3 -left-2 text-base">
                                        🕒
                                      </span>
                                    )}
                                    <MemoizedMarkdown
                                      id={`${m.id}-${i}`}
                                      content={part.text.replace(
                                        /^scheduled message: /,
                                        ""
                                      )}
                                    />
                                  </Card>
                                  <p
                                    className={`text-xs text-muted-foreground mt-1 ${
                                      isUser ? "text-right" : "text-left"
                                    }`}
                                  >
                                    {formatTime(
                                      m.metadata?.createdAt
                                        ? new Date(
                                            isNaN(Number(m.metadata.createdAt))
                                              ? m.metadata.createdAt
                                              : Number(m.metadata.createdAt)
                                          )
                                        : new Date()
                                    )}
                                    {!isUser ? (
                                      <span
                                        onClick={() => {
                                          const logId = m.metadata?.chatLogId;
                                          if (logId) {
                                            setOptimisticFlaggedIds((prev) =>
                                              prev.includes(logId)
                                                ? prev
                                                : [...prev, logId]
                                            );
                                            agent.send(
                                              JSON.stringify({
                                                type: "flag-bad-response",
                                                chatLogId: logId,
                                              })
                                            );
                                          }
                                        }}
                                        className={`text-sm float-right flag-response`}
                                      >
                                        <span
                                          className={`bad-response-text ${isMobile ? "is-mobile" : ""} ${state.flaggedChatLogIds?.includes(m.metadata?.chatLogId as number) || optimisticFlaggedIds.includes(m.metadata?.chatLogId as number) ? "flagged" : ""}`}
                                        >
                                          {state.flaggedChatLogIds?.includes(
                                            m.metadata?.chatLogId as number
                                          ) ||
                                          optimisticFlaggedIds.includes(
                                            m.metadata?.chatLogId as number
                                          )
                                            ? "Flagged"
                                            : "Flag Bad Response"}
                                        </span>
                                        <img
                                          className="inline-block ml-1"
                                          src={iconFlagResponse}
                                          alt="Flag Response"
                                        />
                                      </span>
                                    ) : null}
                                  </p>
                                </div>
                              );
                            }

                            if (
                              isStaticToolUIPart(part) &&
                              // messageIsAssistant(m) &&
                              state.toolMeta?.[part.type.replace("tool-", "")]
                                ?.showInvocationCard
                            ) {
                              const toolCallId = part.toolCallId;
                              const toolName = part.type.replace("tool-", "");
                              const needsConfirmation =
                                toolsRequiringConfirmation.includes(
                                  toolName as keyof typeof tools
                                );

                              return (
                                <ToolInvocationCard
                                  // biome-ignore lint/suspicious/noArrayIndexKey: using index is safe here as the array is static
                                  key={`${toolCallId}-${i}`}
                                  toolUIPart={part}
                                  toolCallId={toolCallId}
                                  needsConfirmation={needsConfirmation}
                                  onSubmit={({ toolCallId, result }) => {
                                    addToolResult({
                                      tool: part.type.replace("tool-", ""),
                                      toolCallId,
                                      output: result,
                                    });
                                  }}
                                  toolMeta={state.toolMeta[toolName] || {}}
                                  userEmail={userEmail}
                                />
                              );
                            }
                            return null;
                          })}
                          {/* If there are no parts but we have a message, it is waiting for the response so we can show a loading message */}
                          {isLastMessage &&
                          (status === "streaming" || status === "submitted") &&
                          ((m.parts && m.parts.length == 0) ||
                            (!m.parts?.some((part) => part.type === "text") &&
                              !m.parts?.some(
                                (part) =>
                                  isStaticToolUIPart(part) &&
                                  state.toolMeta?.[
                                    part.type.replace("tool-", "")
                                  ]?.showInvocationCard
                              )) ||
                            m.parts?.some(
                              (part) =>
                                part.type === "reasoning" &&
                                part.state == "streaming"
                            )) ? (
                            <div key="loading-card">
                              <Card
                                className={`p-3 rounded-md user-bg ${
                                  isUser
                                    ? "rounded-br-none"
                                    : "rounded-bl-none border-assistant-border assistant-bg"
                                } relative`}
                              >
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <span className="animate-spin inline-block w-4 h-4 border-2 border-neutral-300 border-t-transparent rounded-full"></span>
                                  <LoadingMessageQueue
                                    loadingMessage={state.loadingMessage}
                                  >
                                    {(msg) => msg || "Thinking..."}
                                  </LoadingMessageQueue>
                                </div>
                              </Card>
                              <p
                                className={`text-xs text-muted-foreground mt-1 ${
                                  isUser ? "text-right" : "text-left"
                                }`}
                              >
                                {formatTime(
                                  m.metadata?.createdAt
                                    ? new Date(
                                        isNaN(Number(m.metadata.createdAt))
                                          ? m.metadata.createdAt
                                          : Number(m.metadata.createdAt)
                                      )
                                    : new Date()
                                )}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {errorOutput && (
              <Card
                key="error-card"
                className="p-4 mb-3 w-full max-w-[500px] rounded-md assistant-bg border-[#F48120]/30 overflow-hidden"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {errorOutput}
                </div>
              </Card>
            )}
          </div>

          {/* Input Area */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitHandler(e);
              setErrorOutput(false); // Reset error output on submission
              setTextareaHeight("auto"); // Reset height after submission
            }}
            className={`chat-bot-footer p-4 bg-neutral-50 absolute bottom-0 left-0 right-0 z-10 border-t border-neutral-300 ${isMobile ? "landscape:px-16" : ""}`}
          >
            <div className="flex items-center gap-2">
              <div className="flex-1 flex flex-col gap-3 relative">
                <Textarea
                  disabled={
                    pendingToolCallConfirmation || !(status === "ready")
                  }
                  placeholder={
                    pendingToolCallConfirmation
                      ? "Please respond to the tool confirmation above..."
                      : "Type your question..."
                  }
                  className={`bg-white flex w-full border border-neutral-200 px-3 py-2 ring-offset-background placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm min-h-[24px] max-h-[calc(75dvh)] overflow-hidden resize-none rounded-2xl !text-base ${isMobile ? "pr-16 pb-12" : "pb-10"}`}
                  value={agentInput}
                  maxLength={1500}
                  onChange={(e) => {
                    handleAgentInputChange(e);
                    // Auto-resize the textarea
                    e.target.style.height = "auto";
                    e.target.style.height = `${e.target.scrollHeight}px`;
                    setTextareaHeight(`${e.target.scrollHeight}px`);
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !e.nativeEvent.isComposing
                    ) {
                      e.preventDefault();
                      submitHandler(e as unknown as React.FormEvent);
                      setErrorOutput(false); // Reset error output on Enter submission
                      setTextareaHeight("auto"); // Reset height on Enter submission
                    }
                  }}
                  rows={2}
                  style={{ height: textareaHeight }}
                />
                <div className={`absolute ${isMobile ? "bottom-1 right-1" : "bottom-0 right-0"} p-2 w-fit flex flex-row justify-end`}>
                  {status === "submitted" || status === "streaming" ? (
                    <button
                      type="button"
                      onClick={stopHandler}
                      className={`submit-btn inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full border border-neutral-200 ${isMobile ? "[&_svg]:size-6 p-2.5 min-h-11 min-w-11" : "[&_svg]:size-4 p-1.5 h-fit"}`}
                      aria-label="Stop generation"
                    >
                      <Stop size={16} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className={`submit-btn inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full border border-neutral-200 ${isMobile ? "[&_svg]:size-6 p-2.5 min-h-11 min-w-11" : "[&_svg]:size-4 p-1.5 h-fit"}`}
                      disabled={
                        pendingToolCallConfirmation ||
                        !agentInput.trim() ||
                        !(status === "ready")
                      }
                      aria-label="Send message"
                    >
                      <PaperPlaneTilt size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
        {/* Reconnecting Overlay */}
        {isReconnecting && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <Card className="p-6 bg-white w-3/4">
              <div className="flex flex-col items-center gap-4">
                <span className="animate-spin inline-block w-8 h-8 border-4 border-neutral-300 border-t-transparent rounded-full" />
                <p className="text-lg font-semibold">Reconnecting...</p>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
